import React, { useId, useState } from 'react';
import { Box, Button, ButtonBase, LinearProgress, Popover, Typography } from '@mui/material';
import { ArrowDropDown, AutoAwesomeOutlined, CheckCircleOutline, RadioButtonUnchecked } from '@mui/icons-material';

/**
 * Inky prompts that fix each template quality issue (ids from utils/templateQualityScore).
 * Phrased as complete requests so "Fix with Inky" can send them straight away.
 */
const QUALITY_FIX_PROMPTS = {
  'missing-prompts': 'Add AI fields to this template wherever a section needs project-specific content, each with a clear instruction.',
  'single-prompt': 'Add AI fields to each major section of this template so the generated content is balanced across sections.',
  'weak-prompts': 'Make every AI field instruction in this template more specific: say what to cover, how much detail to give and which source to use. Do not change the fixed text.',
  'duplicate-prompts': 'Find AI fields in this template that repeat the same instruction and give each one a distinct instruction for its section.',
  'missing-headings': 'Add clear section headings to this template so the generated document has a clear structure.',
  'skipped-heading-level': 'Fix the heading hierarchy in this template so no heading levels are skipped.',
  'low-prompt-coverage': 'Add AI fields under the sections that only have fixed text, so every major section gets project-specific content.',
  'empty-paragraphs': 'Remove the empty paragraphs from this template without changing anything else.',
  'malformed-braces': 'Find and fix unmatched curly braces in this template so every AI instruction becomes a proper AI field.',
  'empty-content': 'Draft a starting structure for this template: section headings, short fixed text and an AI field for each section.',
};

/**
 * The Inky request that fixes a quality issue.
 * @param {{id: string, message: string}} issue - Quality issue.
 * @returns {string} Prompt.
 */
export function qualityFixPrompt(issue) {
  return QUALITY_FIX_PROMPTS[issue?.id] || `Fix this template quality issue: ${issue?.message || ''}`.trim();
}

const STATUS_COLORS = {
  success: { dot: 'var(--q-success)', bar: 'var(--q-success)' },
  warning: { dot: 'var(--q-warning)', bar: 'var(--q-warning)' },
  error: { dot: 'var(--q-danger)', bar: 'var(--q-danger)' },
};

/**
 * Header chip with the template quality score ("Quality 82 ▾"). It opens the checklist in a
 * popover; each open item has a one-click "Fix with Inky" action that sends a matching request
 * to the Inky panel.
 * @param {object} props - Props.
 * @param {ReturnType<import('../../utils/templateQualityScore').analyzeTemplateQuality>} props.quality - Quality result.
 * @param {(prompt: string) => void} props.onFix - Send a fix request to Inky.
 * @returns {JSX.Element} Chip and popover.
 */
export default function TemplateQualityChip({ quality, onFix }) {
  const [anchor, setAnchor] = useState(null);
  const baseId = useId();
  const popoverId = `${baseId}-quality`;
  const titleId = `${baseId}-quality-title`;
  const colors = STATUS_COLORS[quality.color] || STATUS_COLORS.warning;
  const open = Boolean(anchor);
  const openIssues = quality.issues.slice(0, 5);

  return (
    <>
      <ButtonBase
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={open ? popoverId : undefined}
        aria-label={`Template quality ${quality.score} of 100, ${quality.label}. Show checklist`}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          height: 32,
          pl: 1.25,
          pr: 0.5,
          gap: 0.75,
          flex: 'none',
          borderRadius: 'var(--q-radius-pill, 999px)',
          border: '1px solid var(--q-border, #E3E0DA)',
          bgcolor: open ? 'var(--q-control-selected, #DCD8D0)' : 'var(--q-surface, #FFFFFF)',
          color: 'var(--q-text-2, #3D3A35)',
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'inherit',
          '&:hover': { bgcolor: 'var(--q-hover, #ECEAE5)' },
          '&.Mui-focusVisible': { outline: '2px solid var(--q-focus-ring, #4F3BD6)', outlineOffset: 2 },
        }}
      >
        <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.dot, flex: 'none' }} />
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Quality</Box>
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{quality.score}</Box>
        <ArrowDropDown sx={{ fontSize: 20, color: 'var(--q-muted, #5E5A54)' }} />
      </ButtonBase>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': titleId,
            sx: { mt: 0.75, p: 2, width: 'min(360px, calc(100vw - 32px))', borderRadius: 'var(--q-radius-xl, 12px)' },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Typography id={titleId} component="h2" sx={{ flex: 1, fontSize: 15, fontWeight: 600 }}>
            Template quality
          </Typography>
          <Typography component="span" sx={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            {quality.score}
          </Typography>
          <Typography component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>/ 100 · {quality.label}</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={quality.score}
          aria-label="Template quality score"
          sx={{ height: 6, borderRadius: 3, bgcolor: 'var(--q-border-subtle)', '& .MuiLinearProgress-bar': { bgcolor: colors.bar } }}
        />
        <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 1.5, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {quality.checks.map((check) => (
            <Box component="li" key={check} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', fontSize: 13 }}>
              <CheckCircleOutline sx={{ fontSize: 16, color: 'var(--q-success)', mt: '1px' }} />
              <span>{check}</span>
            </Box>
          ))}
          {openIssues.map((issue) => (
            <Box component="li" key={issue.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', fontSize: 13 }}>
              <RadioButtonUnchecked sx={{ fontSize: 16, color: issue.severity === 'error' ? 'error.main' : 'text.secondary', mt: '1px' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <span>{issue.message}</span>
                <Box>
                  <Button
                    size="small"
                    startIcon={<AutoAwesomeOutlined sx={{ fontSize: '15px !important' }} />}
                    onClick={() => {
                      setAnchor(null);
                      onFix(qualityFixPrompt(issue));
                    }}
                    sx={{
                      mt: 0.5,
                      height: 28,
                      px: 1.25,
                      borderRadius: 'var(--q-radius-pill, 999px)',
                      bgcolor: 'var(--q-ai-tint, #F9F8FE)',
                      border: '1px dashed var(--q-ai-border, #C9C1F2)',
                      color: 'var(--q-ai-text, #3A28B8)',
                      fontSize: 12.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { bgcolor: 'var(--q-ai-tint-strong, #F2EFFD)' },
                    }}
                  >
                    Fix with Inky
                  </Button>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
}
